import {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react"

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material"

import AddRoundedIcon from "@mui/icons-material/AddRounded"
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded"
import EditRoundedIcon from "@mui/icons-material/EditRounded"
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined"
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded"
import SearchRoundedIcon from "@mui/icons-material/SearchRounded"
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded"

import MainLayout from "../../layouts/MainLayout"
import api from "../../services/api"

import {
  activateProductionBom,
  createProductionBom,
  deactivateProductionBom,
  extractProductionError,
  getProductionBomById,
  getProductionBomFormData,
  getProductionBoms,
  normalizeProductionArray,
  updateProductionBom,
} from "../../services/productionService"


/* =========================================================
   CONSTANTES
   ========================================================= */

const EMPTY_ITEM = {
  material_product_id: "",
  quantity: 1,
  unit: "",
  waste_percent: 0,
  notes: "",
}

const EMPTY_FORM = {
  id: null,
  code: "",

  finished_product_id: "",

  output_quantity: 1,
  output_unit: "",

  version: "1.0",

  name: "",
  notes: "",

  is_active: true,

  items: [
    {
      ...EMPTY_ITEM,
    },
  ],
}


/* =========================================================
   HELPERS
   ========================================================= */

function toNumber(
  value,
  fallback = 0
) {
  const number = Number(value)

  return Number.isFinite(number)
    ? number
    : fallback
}


function normalizeProductType(
  value = ""
) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replaceAll("-", "_")
    .replaceAll(" ", "_")
}


function isFinishedGood(
  product = {}
) {
  const value =
    normalizeProductType(
      product.product_type ||
        product.type ||
        product.nature
    )

  return [
    "FINISHED_GOOD",
    "FINISHED_GOODS",
    "FINISHED_PRODUCT",
    "FINISHED_PRODUCTS",
    "PRODUIT_FINI",
    "PRODUITS_FINIS",
  ].includes(value)
}


/*
 * Les composants d'un BOM ne sont pas
 * uniquement des matières premières chimiques.
 *
 * Ils peuvent être :
 * - matière première
 * - composant
 * - emballage
 * - consommable
 *
 * On exclut principalement les produits finis.
 */
function canBeBomComponent(
  product = {}
) {
  if (isFinishedGood(product)) {
    return false
  }

  if (
    product.can_be_bom_component ===
      true ||
    product.can_enter_bom === true ||
    product.is_bom_component === true
  ) {
    return true
  }

  const value =
    normalizeProductType(
      product.product_type ||
        product.type ||
        product.nature
    )

  return [
    "RAW_MATERIAL",
    "RAW_MATERIALS",
    "MATIERE_PREMIERE",
    "MATIERES_PREMIERES",

    "COMPONENT",
    "COMPONENTS",
    "COMPOSANT",
    "COMPOSANTS",

    "PACKAGING",
    "EMBALLAGE",
    "EMBALLAGES",

    "CONSUMABLE",
    "CONSUMABLES",
    "CONSOMMABLE",
    "CONSOMMABLES",

    "MATERIAL",
    "MATERIALS",
    "MATIERE",
    "MATIERES",
  ].includes(value)
}


function productLabel(
  product = {}
) {
  const code =
    product.code || ""

  const name =
    product.name || ""

  if (code && name) {
    return `${code} - ${name}`
  }

  return (
    name ||
    code ||
    `Produit #${product.id}`
  )
}


function getProductUnit(
  product = {}
) {
  return (
    product.unit ||
    product.management_unit ||
    product.unit_name ||
    ""
  )
}


function unitLabel(
  value = ""
) {
  const normalized =
    String(value || "")
      .trim()
      .toLowerCase()

  const labels = {
    piece: "Pièce",
    pieces: "Pièce",
    pcs: "Pièce",

    kg: "Kilogramme",
    kilogram: "Kilogramme",
    kilogramme: "Kilogramme",

    g: "Gramme",
    gramme: "Gramme",

    l: "Litre",
    litre: "Litre",
    liter: "Litre",

    ml: "Millilitre",

    m: "Mètre",
    meter: "Mètre",
    metre: "Mètre",

    m2: "Mètre carré",
    m3: "Mètre cube",

    tonne: "Tonne",
    ton: "Tonne",

    carton: "Carton",

    box: "Boîte",
    boite: "Boîte",

    bag: "Sac",
    sac: "Sac",

    roll: "Rouleau",
    rouleau: "Rouleau",
  }

  return (
    labels[normalized] ||
    value ||
    "-"
  )
}


function statusLabel(
  bom = {}
) {
  return bom.is_active === false
    ? "Inactive"
    : "Active"
}


function normalizeBomVersion(
  value = ""
) {
  const version =
    String(value || "1.0").trim()

  return version || "1.0"
}


function bomGroupKey(
  bom = {}
) {
  return `${
    Number(
      bom.finished_product_id || 0
    )
  }::${
    normalizeBomVersion(
      bom.version
    )
  }`
}


/* =========================================================
   PAGE
   ========================================================= */

export default function ProductionBomsPage() {
  const [
    loading,
    setLoading,
  ] = useState(true)

  const [
    saving,
    setSaving,
  ] = useState(false)

  const [
    boms,
    setBoms,
  ] = useState([])

  const [
    products,
    setProducts,
  ] = useState([])

  const [
    search,
    setSearch,
  ] = useState("")

  const [
    statusFilter,
    setStatusFilter,
  ] = useState("ALL")

  const [
    dialogOpen,
    setDialogOpen,
  ] = useState(false)

  const [
    viewMode,
    setViewMode,
  ] = useState(false)

  const [
    form,
    setForm,
  ] = useState({
    ...EMPTY_FORM,
    items: [
      {
        ...EMPTY_ITEM,
      },
    ],
  })

  const [
    snackbar,
    setSnackbar,
  ] = useState({
    open: false,
    severity: "success",
    message: "",
  })


  /* =======================================================
     CATALOGUE COMPATIBLE PRODUCTION
     ======================================================= */

  const finishedProducts =
    useMemo(
      () =>
        products.filter(
          isFinishedGood
        ),
      [products]
    )


  const bomComponents =
    useMemo(
      () => {
        const compatible =
          products.filter(
            canBeBomComponent
          )

        /*
         * Compatibilité ancienne base :
         * si aucune nature n'est reconnue,
         * on prend tous les articles
         * sauf les produits finis.
         */
        if (compatible.length > 0) {
          return compatible
        }

        return products.filter(
          (product) =>
            !isFinishedGood(product)
        )
      },
      [products]
    )


  /* =======================================================
     CHARGEMENT
     ======================================================= */

  const loadData =
    useCallback(
      async () => {
        setLoading(true)

        try {
          const [
            bomResponse,
            formDataResponse,
          ] =
            await Promise.all([
              getProductionBoms(),
              getProductionBomFormData(),
            ])

          setBoms(
            normalizeProductionArray(
              bomResponse,
              "boms"
            )
          )

          const productList =
            normalizeProductionArray(
              formDataResponse?.products,
              "products"
            )

          setProducts(
            productList.length
              ? productList
              : normalizeProductionArray(
                  formDataResponse,
                  "products"
                )
          )
        } catch (error) {
          setSnackbar({
            open: true,
            severity: "error",
            message:
              extractProductionError(
                error,
                "Impossible de charger les nomenclatures."
              ),
          })
        } finally {
          setLoading(false)
        }
      },
      []
    )


  useEffect(() => {
    loadData()
  }, [loadData])


  /* =======================================================
     FILTRAGE
     ======================================================= */

  const filteredBoms =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase()

      return boms.filter(
        (bom) => {
          const haystack = [
            bom.code,
            bom.name,
            bom.finished_product_code,
            bom.finished_product_name,
            bom.version,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase()

          const matchesSearch =
            !term ||
            haystack.includes(term)

          const matchesStatus =
            statusFilter === "ALL" ||
            (
              statusFilter ===
                "ACTIVE" &&
              bom.is_active !== false
            ) ||
            (
              statusFilter ===
                "INACTIVE" &&
              bom.is_active === false
            )

          return (
            matchesSearch &&
            matchesStatus
          )
        }
      )
    }, [
      boms,
      search,
      statusFilter,
    ])


  /* =======================================================
     KPI
     ======================================================= */

  const dashboard =
    useMemo(() => {
      const active =
        boms.filter(
          (bom) =>
            bom.is_active !== false
        ).length

      const materialLines =
        boms.reduce(
          (total, bom) =>
            total +
            toNumber(
              bom.material_count ??
                bom.items_count ??
                bom.components_count ??
                bom.materials_count ??
                bom.items?.length,
              0
            ),
          0
        )

      return {
        total:
          boms.length,

        active,

        inactive:
          boms.length - active,

        materialLines,
      }
    }, [boms])


  /* =======================================================
     DOUBLONS ACTIFS
     ======================================================= */

  const duplicateGroups =
    useMemo(() => {
      const groups = new Map()

      boms
        .filter(
          (bom) =>
            bom.is_active !== false
        )
        .forEach((bom) => {
          const key =
            bomGroupKey(bom)

          const current =
            groups.get(key) || []

          current.push(bom)

          groups.set(
            key,
            current
          )
        })

      return Array.from(
        groups.values()
      )
        .filter(
          (group) =>
            group.length > 1
        )
        .map((group) =>
          [...group].sort(
            (a, b) =>
              Number(b.id || 0) -
              Number(a.id || 0)
          )
        )
    }, [boms])


  const duplicateBomCount =
    useMemo(
      () =>
        duplicateGroups.reduce(
          (total, group) =>
            total +
            group.length,
          0
        ),
      [duplicateGroups]
    )


  const handleConsolidateDuplicates =
    async () => {
      if (
        duplicateGroups.length === 0
      ) {
        return
      }

      try {
        setSaving(true)

        let mergedGroups = 0
        let mergedComponents = 0

        for (
          const group
          of duplicateGroups
        ) {
          const master =
            group[0]

          const response =
            await api.post(
              "/production/boms/consolidate",
              {
                finished_product_id:
                  Number(
                    master.finished_product_id
                  ),

                version:
                  normalizeBomVersion(
                    master.version
                  ),
              }
            )

          mergedGroups += 1
          mergedComponents +=
            toNumber(
              response?.data
                ?.merged_components,
              0
            )
        }

        setSnackbar({
          open: true,
          severity: "success",
          message:
            `${mergedGroups} groupe${
              mergedGroups > 1
                ? "s"
                : ""
            } de doublons consolidé${
              mergedGroups > 1
                ? "s"
                : ""
            }. ${
              mergedComponents > 0
                ? `${mergedComponents} composant(s) conservé(s).`
                : ""
            }`,
        })

        await loadData()
      } catch (error) {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            extractProductionError(
              error,
              "Impossible de consolider les doublons."
            ),
        })
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     PRODUIT FINI SÉLECTIONNÉ
     ======================================================= */

  const selectedFinishedProduct =
    useMemo(
      () =>
        products.find(
          (product) =>
            Number(product.id) ===
            Number(
              form.finished_product_id
            )
        ),
      [
        products,
        form.finished_product_id,
      ]
    )


  /* =======================================================
     FORM
     ======================================================= */

  const resetForm = () => {
    setForm({
      ...EMPTY_FORM,
      items: [
        {
          ...EMPTY_ITEM,
        },
      ],
    })
  }


  const openCreateDialog = () => {
    resetForm()
    setViewMode(false)
    setDialogOpen(true)
  }


  const closeDialog = () => {
    if (saving) {
      return
    }

    setDialogOpen(false)
    setViewMode(false)
    resetForm()
  }


  const handleFinishedProductChange = (
    event
  ) => {
    const productId =
      event.target.value

    const product =
      products.find(
        (item) =>
          Number(item.id) ===
          Number(productId)
      )

    setForm(
      (previous) => ({
        ...previous,

        finished_product_id:
          productId,

        output_unit:
          getProductUnit(
            product
          ) ||
          previous.output_unit,
      })
    )
  }


  const addMaterialLine = () => {
    setForm(
      (previous) => ({
        ...previous,

        items: [
          ...previous.items,
          {
            ...EMPTY_ITEM,
          },
        ],
      })
    )
  }


  const removeMaterialLine = (
    index
  ) => {
    setForm(
      (previous) => {
        if (
          previous.items.length <= 1
        ) {
          return previous
        }

        return {
          ...previous,

          items:
            previous.items.filter(
              (
                _,
                itemIndex
              ) =>
                itemIndex !==
                index
            ),
        }
      }
    )
  }


  const updateMaterialLine = (
    index,
    field,
    value
  ) => {
    setForm(
      (previous) => {
        const items =
          previous.items.map(
            (
              currentItem,
              itemIndex
            ) =>
              itemIndex === index
                ? {
                    ...currentItem,
                    [field]: value,
                  }
                : currentItem
          )

        if (
          field ===
          "material_product_id"
        ) {
          const product =
            products.find(
              (item) =>
                Number(item.id) ===
                Number(value)
            )

          items[index] = {
            ...items[index],

            unit:
              getProductUnit(
                product
              ) ||
              items[index].unit,
          }
        }

        return {
          ...previous,
          items,
        }
      }
    )
  }


  /* =======================================================
     OUVERTURE BOM
     ======================================================= */

  const openExistingBom =
    async (
      bomId,
      readOnly = false
    ) => {
      try {
        setSaving(true)

        const data =
          await getProductionBomById(
            bomId
          )

        const items =
          normalizeProductionArray(
            data?.items,
            "items"
          )

        setForm({
          id:
            data.id,

          code:
            data.code || "",

          name:
            data.name || "",

          finished_product_id:
            data.finished_product_id ||
            data.product_id ||
            "",

          output_quantity:
            toNumber(
              data.output_quantity,
              1
            ),

          output_unit:
            data.output_unit ||
            data.finished_product_unit ||
            data.unit ||
            "",

          version:
            data.version ||
            "1.0",

          notes:
            data.notes ||
            "",

          is_active:
            data.is_active !== false,

          items:
            items.length
              ? items.map(
                  (item) => ({
                    id:
                      item.id,

                    material_product_id:
                      item.material_product_id ||
                      item.product_id ||
                      "",

                    quantity:
                      toNumber(
                        item.required_quantity ??
                          item.quantity,
                        1
                      ),

                    unit:
                      item.unit ||
                      item.material_unit ||
                      "",

                    waste_percent:
                      toNumber(
                        item.waste_percent,
                        0
                      ),

                    notes:
                      item.notes ||
                      "",
                  })
                )
              : [
                  {
                    ...EMPTY_ITEM,
                  },
                ],
        })

        setViewMode(readOnly)
        setDialogOpen(true)
      } catch (error) {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            extractProductionError(
              error
            ),
        })
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     VALIDATION
     ======================================================= */

  const validateForm = () => {
    if (
      !form.finished_product_id
    ) {
      return (
        "Sélectionnez le produit fini."
      )
    }

    if (
      toNumber(
        form.output_quantity
      ) <= 0
    ) {
      return (
        "La quantité produite doit être supérieure à zéro."
      )
    }

    if (
      !Array.isArray(form.items) ||
      form.items.length === 0
    ) {
      return (
        "Ajoutez au moins une matière ou un composant."
      )
    }

    for (
      let index = 0;
      index <
      form.items.length;
      index += 1
    ) {
      const item =
        form.items[index]

      if (
        !item.material_product_id
      ) {
        return (
          `Sélectionnez le composant de la ligne ${
            index + 1
          }.`
        )
      }

      if (
        toNumber(
          item.quantity
        ) <= 0
      ) {
        return (
          `La quantité de la ligne ${
            index + 1
          } doit être supérieure à zéro.`
        )
      }

      if (
        toNumber(
          item.waste_percent
        ) < 0
      ) {
        return (
          `Le taux de perte de la ligne ${
            index + 1
          } ne peut pas être négatif.`
        )
      }
    }

    const componentIds =
      form.items.map(
        (item) =>
          String(
            item.material_product_id
          )
      )

    if (
      new Set(componentIds).size !==
      componentIds.length
    ) {
      return (
        "Un même composant apparaît plusieurs fois dans la nomenclature."
      )
    }

    if (
      form.items.some(
        (item) =>
          Number(
            item.material_product_id
          ) ===
          Number(
            form.finished_product_id
          )
      )
    ) {
      return (
        "Le produit fini ne peut pas être son propre composant."
      )
    }

    if (
      !form.id &&
      form.is_active !== false
    ) {
      const version =
        normalizeBomVersion(
          form.version
        )

      const existingActive =
        boms.find(
          (bom) =>
            bom.is_active !== false &&
            Number(
              bom.finished_product_id
            ) ===
              Number(
                form.finished_product_id
              ) &&
            normalizeBomVersion(
              bom.version
            ) === version
        )

      if (existingActive) {
        return (
          `Une nomenclature active existe déjà pour ce produit en version ${version}. ` +
          "Modifiez-la ou créez une nouvelle version."
        )
      }
    }

    return ""
  }


  /* =======================================================
     SAVE
     ======================================================= */

  const handleSave =
    async () => {
      const validationError =
        validateForm()

      if (validationError) {
        setSnackbar({
          open: true,
          severity: "warning",
          message:
            validationError,
        })

        return
      }

      /*
       * IMPORTANT :
       * On conserve ici les noms métier.
       *
       * productionService.js effectue ensuite
       * une dernière normalisation de sécurité.
       */
      const payload = {
        finished_product_id:
          Number(
            form.finished_product_id
          ),

        output_quantity:
          toNumber(
            form.output_quantity,
            1
          ),

        output_unit:
          form.output_unit ||
          getProductUnit(
            selectedFinishedProduct
          ) ||
          null,

        version:
          form.version
            ?.trim() ||
          "1.0",

        name:
          form.name
            ?.trim() ||
          null,

        notes:
          form.notes
            ?.trim() ||
          null,

        is_active:
          form.is_active !== false,

        items:
          form.items.map(
            (item) => ({
              material_product_id:
                Number(
                  item.material_product_id
                ),

              quantity:
                toNumber(
                  item.quantity
                ),

              unit:
                item.unit ||
                null,

              waste_percent:
                toNumber(
                  item.waste_percent,
                  0
                ),

              notes:
                item.notes
                  ?.trim() ||
                null,
            })
          ),
      }

      try {
        setSaving(true)

        let result

        if (form.id) {
          result =
            await updateProductionBom(
              form.id,
              payload
            )
        } else {
          result =
            await createProductionBom(
              payload
            )
        }

        setSnackbar({
          open: true,
          severity: "success",
          message:
            result?.message ||
            (
              form.id
                ? "Nomenclature modifiée avec succès."
                : "Nomenclature créée avec succès."
            ),
        })

        setDialogOpen(false)

        resetForm()

        await loadData()
      } catch (error) {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            extractProductionError(
              error,
              "Impossible d'enregistrer la nomenclature."
            ),
        })
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     ACTIVATION
     ======================================================= */

  const handleToggleStatus =
    async (bom) => {
      try {
        setSaving(true)

        if (
          bom.is_active === false
        ) {
          await activateProductionBom(
            bom.id
          )
        } else {
          await deactivateProductionBom(
            bom.id
          )
        }

        setSnackbar({
          open: true,
          severity: "success",
          message:
            bom.is_active === false
              ? "Nomenclature activée."
              : "Nomenclature désactivée.",
        })

        await loadData()
      } catch (error) {
        setSnackbar({
          open: true,
          severity: "error",
          message:
            extractProductionError(
              error
            ),
        })
      } finally {
        setSaving(false)
      }
    }


  /* =======================================================
     RENDER
     ======================================================= */

  return (
    <MainLayout>
      <Box
        sx={{
          width: "100%",
          p: {
            xs: 1.5,
            sm: 2,
            md: 3,
          },
        }}
      >
        {/* HEADER */}

        <Stack
          direction={{
            xs: "column",
            md: "row",
          }}
          spacing={2}
          justifyContent="space-between"
          alignItems={{
            xs: "stretch",
            md: "center",
          }}
          mb={2}
        >
          <Box>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
            >
              <Inventory2OutlinedIcon
                color="primary"
              />

              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  fontSize: {
                    xs: 26,
                    md: 32,
                  },
                }}
              >
                Nomenclatures de production
              </Typography>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              mt={0.5}
            >
              Définition des matières,
              composants, consommables et
              emballages nécessaires à la
              fabrication d'un produit.
            </Typography>
          </Box>

          <Stack
            direction={{
              xs: "column",
              sm: "row",
            }}
            spacing={1}
          >
            <Button
              variant="outlined"
              startIcon={
                <RefreshRoundedIcon />
              }
              onClick={loadData}
              disabled={loading}
            >
              Actualiser
            </Button>

            <Button
              variant="contained"
              startIcon={
                <AddRoundedIcon />
              }
              onClick={
                openCreateDialog
              }
            >
              Nouvelle nomenclature
            </Button>
          </Stack>
        </Stack>


        <Alert
          severity="info"
          sx={{ mb: 2 }}
        >
          <strong>
            Production :
          </strong>{" "}
          Produit fini → Nomenclature
          (BOM) → Ordre de production →
          Réservation → Consommation →
          Fabrication → Entrée du produit
          fini en stock.
        </Alert>


        {duplicateGroups.length > 0 && (
          <Alert
            severity="warning"
            icon={
              <WarningAmberRoundedIcon />
            }
            sx={{
              mb: 2,
              alignItems: "center",
            }}
            action={
              <Button
                color="warning"
                variant="contained"
                size="small"
                disabled={saving}
                onClick={
                  handleConsolidateDuplicates
                }
              >
                {saving
                  ? "Consolidation..."
                  : "Fusionner les doublons"}
              </Button>
            }
          >
            <Typography
              variant="body2"
              fontWeight={700}
            >
              {duplicateBomCount} nomenclature(s)
              active(s) en conflit détectée(s)
              dans {duplicateGroups.length} groupe(s).
            </Typography>

            <Typography
              variant="body2"
            >
              RLSys conservera la BOM active
              la plus récente, fusionnera les
              composants uniques et désactivera
              automatiquement les anciennes BOM
              du même produit et de la même
              version.
            </Typography>
          </Alert>
        )}


        {/* KPI */}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              lg: "repeat(4, 1fr)",
            },
            gap: 1.5,
            mb: 2,
          }}
        >
          {[
            [
              "Nomenclatures",
              dashboard.total,
            ],
            [
              "Actives",
              dashboard.active,
            ],
            [
              "Inactives",
              dashboard.inactive,
            ],
            [
              "Lignes composants",
              dashboard.materialLines,
            ],
          ].map(
            ([label, value]) => (
              <Paper
                key={label}
                variant="outlined"
                sx={{ p: 2 }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  {label}
                </Typography>

                <Typography variant="h4">
                  {value}
                </Typography>
              </Paper>
            )
          )}
        </Box>


        {/* FILTERS */}

        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 2,
          }}
        >
          <Stack
            direction={{
              xs: "column",
              md: "row",
            }}
            spacing={1.5}
          >
            <TextField
              fullWidth
              value={search}
              onChange={(event) =>
                setSearch(
                  event.target.value
                )
              }
              placeholder="Rechercher une nomenclature, un produit, un code..."
              InputProps={{
                startAdornment: (
                  <SearchRoundedIcon
                    sx={{
                      mr: 1,
                      color:
                        "text.secondary",
                    }}
                  />
                ),
              }}
            />

            <FormControl
              sx={{
                minWidth: {
                  xs: "100%",
                  md: 220,
                },
              }}
            >
              <InputLabel>
                Statut
              </InputLabel>

              <Select
                label="Statut"
                value={
                  statusFilter
                }
                onChange={(event) =>
                  setStatusFilter(
                    event.target.value
                  )
                }
              >
                <MenuItem value="ALL">
                  Tous
                </MenuItem>

                <MenuItem value="ACTIVE">
                  Actives
                </MenuItem>

                <MenuItem value="INACTIVE">
                  Inactives
                </MenuItem>
              </Select>
            </FormControl>

            <Button
              variant="outlined"
              onClick={() => {
                setSearch("")
                setStatusFilter("ALL")
              }}
            >
              Réinitialiser
            </Button>
          </Stack>
        </Paper>


        {/* TABLE */}

        <Paper
          variant="outlined"
          sx={{
            overflow: "hidden",
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography
              variant="h6"
              fontWeight={700}
            >
              Liste des nomenclatures
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
            >
              Recettes techniques utilisées
              pour calculer automatiquement
              les besoins de production.
            </Typography>
          </Box>

          <Divider />

          {loading ? (
            <Box
              sx={{
                py: 8,
                display: "flex",
                justifyContent:
                  "center",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table
                size="small"
                sx={{
                  minWidth: 1000,
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      Code
                    </TableCell>

                    <TableCell>
                      Produit fini
                    </TableCell>

                    <TableCell>
                      Version
                    </TableCell>

                    <TableCell>
                      Production de référence
                    </TableCell>

                    <TableCell>
                      Composants
                    </TableCell>

                    <TableCell>
                      Statut
                    </TableCell>

                    <TableCell align="right">
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredBoms.length ===
                  0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 7 }}
                      >
                        <ScienceOutlinedIcon
                          sx={{
                            fontSize: 44,
                            color:
                              "text.disabled",
                          }}
                        />

                        <Typography
                          mt={1}
                        >
                          Aucune nomenclature
                          de production.
                        </Typography>

                        <Typography
                          variant="body2"
                          color="text.secondary"
                        >
                          Créez la première
                          recette de
                          fabrication.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredBoms.map(
                      (bom) => {
                        const count =
                          bom.material_count ??
                          bom.items_count ??
                          bom.components_count ??
                          bom.materials_count ??
                          0

                        return (
                          <TableRow
                            key={bom.id}
                            hover
                          >
                            <TableCell>
                              <Typography
                                fontWeight={
                                  700
                                }
                              >
                                {bom.code ||
                                  `BOM-${String(
                                    bom.id
                                  ).padStart(
                                    5,
                                    "0"
                                  )}`}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                fontWeight={
                                  600
                                }
                              >
                                {bom.finished_product_name ||
                                  "-"}
                              </Typography>

                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                {bom.finished_product_code ||
                                  ""}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              {bom.version ||
                                "1.0"}
                            </TableCell>

                            <TableCell>
                              {toNumber(
                                bom.output_quantity,
                                1
                              )}{" "}
                              {unitLabel(
                                bom.output_unit ||
                                  bom.finished_product_unit
                              )}
                            </TableCell>

                            <TableCell>
                              <Chip
                                size="small"
                                variant="outlined"
                                label={`${count} composant${
                                  Number(
                                    count
                                  ) > 1
                                    ? "s"
                                    : ""
                                }`}
                              />
                            </TableCell>

                            <TableCell>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                              >
                                <Chip
                                  size="small"
                                  color={
                                    bom.is_active ===
                                    false
                                      ? "default"
                                      : "success"
                                  }
                                  label={statusLabel(
                                    bom
                                  )}
                                />

                                {bom.is_active !==
                                  false &&
                                  toNumber(
                                    bom.active_duplicate_count,
                                    0
                                  ) > 1 && (
                                    <Chip
                                      size="small"
                                      color="warning"
                                      variant="outlined"
                                      label={`Doublon ×${toNumber(
                                        bom.active_duplicate_count,
                                        0
                                      )}`}
                                    />
                                  )}
                              </Stack>
                            </TableCell>

                            <TableCell align="right">
                              <Stack
                                direction="row"
                                spacing={0.5}
                                justifyContent="flex-end"
                              >
                                <Tooltip title="Voir">
                                  <IconButton
                                    size="small"
                                    onClick={() =>
                                      openExistingBom(
                                        bom.id,
                                        true
                                      )
                                    }
                                  >
                                    <VisibilityOutlinedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Tooltip title="Modifier">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() =>
                                      openExistingBom(
                                        bom.id
                                      )
                                    }
                                  >
                                    <EditRoundedIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                <Button
                                  size="small"
                                  variant="outlined"
                                  color={
                                    bom.is_active ===
                                    false
                                      ? "success"
                                      : "warning"
                                  }
                                  disabled={saving}
                                  onClick={() =>
                                    handleToggleStatus(
                                      bom
                                    )
                                  }
                                >
                                  {bom.is_active ===
                                  false
                                    ? "Activer"
                                    : "Désactiver"}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        )
                      }
                    )
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>


        {/* DIALOG */}

        <Dialog
          open={dialogOpen}
          onClose={closeDialog}
          fullWidth
          maxWidth="lg"
          PaperProps={{
            sx: {
              maxHeight: "92vh",
            },
          }}
        >
          <DialogTitle>
            {viewMode
              ? "Détail de la nomenclature"
              : form.id
              ? "Modifier la nomenclature"
              : "Nouvelle nomenclature de production"}
          </DialogTitle>

          <DialogContent dividers>
            <Alert
              severity="info"
              sx={{ mb: 2 }}
            >
              Définissez la quantité de
              référence produite puis tous
              les éléments nécessaires :
              matières premières,
              composants, consommables ou
              emballages.
            </Alert>


            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "2fr 1fr 1fr",
                },
                gap: 2,
              }}
            >
              <FormControl
                fullWidth
                required
                disabled={
                  viewMode ||
                  Boolean(form.id)
                }
              >
                <InputLabel>
                  Produit fini
                </InputLabel>

                <Select
                  label="Produit fini"
                  value={
                    form.finished_product_id
                  }
                  onChange={
                    handleFinishedProductChange
                  }
                >
                  {finishedProducts.map(
                    (product) => (
                      <MenuItem
                        key={product.id}
                        value={product.id}
                      >
                        {productLabel(
                          product
                        )}
                      </MenuItem>
                    )
                  )}
                </Select>
              </FormControl>


              <TextField
                label="Quantité produite"
                type="number"
                required
                disabled={viewMode}
                value={
                  form.output_quantity
                }
                onChange={(event) =>
                  setForm(
                    (previous) => ({
                      ...previous,
                      output_quantity:
                        event.target
                          .value,
                    })
                  )
                }
                inputProps={{
                  min: 0.0001,
                  step: "any",
                }}
              />


              <TextField
                label="Unité"
                disabled
                value={unitLabel(
                  form.output_unit ||
                    getProductUnit(
                      selectedFinishedProduct
                    )
                )}
              />


              <TextField
                label="Version"
                disabled={viewMode}
                value={
                  form.version
                }
                onChange={(event) =>
                  setForm(
                    (previous) => ({
                      ...previous,
                      version:
                        event.target
                          .value,
                    })
                  )
                }
              />


              <FormControl
                disabled={viewMode}
              >
                <InputLabel>
                  Statut
                </InputLabel>

                <Select
                  label="Statut"
                  value={
                    form.is_active
                      ? "ACTIVE"
                      : "INACTIVE"
                  }
                  onChange={(event) =>
                    setForm(
                      (previous) => ({
                        ...previous,
                        is_active:
                          event.target
                            .value ===
                          "ACTIVE",
                      })
                    )
                  }
                >
                  <MenuItem value="ACTIVE">
                    Active
                  </MenuItem>

                  <MenuItem value="INACTIVE">
                    Inactive
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>


            <Divider sx={{ my: 3 }} />


            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "stretch",
                sm: "center",
              }}
              spacing={1}
              mb={2}
            >
              <Box>
                <Typography
                  variant="h6"
                  fontWeight={700}
                >
                  Matières / composants
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Besoins pour produire{" "}
                  <strong>
                    {form.output_quantity ||
                      1}{" "}
                    {unitLabel(
                      form.output_unit
                    )}
                  </strong>
                  .
                </Typography>
              </Box>

              {!viewMode && (
                <Button
                  startIcon={
                    <AddRoundedIcon />
                  }
                  onClick={
                    addMaterialLine
                  }
                >
                  Ajouter composant
                </Button>
              )}
            </Stack>


            <Stack spacing={1.5}>
              {form.items.map(
                (item, index) => {
                  const component =
                    products.find(
                      (product) =>
                        Number(
                          product.id
                        ) ===
                        Number(
                          item.material_product_id
                        )
                    )

                  return (
                    <Paper
                      key={
                        item.id ||
                        `item-${index}`
                      }
                      variant="outlined"
                      sx={{ p: 2 }}
                    >
                      <Box
                        sx={{
                          display:
                            "grid",

                          gridTemplateColumns:
                            {
                              xs: "1fr",
                              md: "2.1fr 0.8fr 0.9fr 0.8fr 1.4fr auto",
                            },

                          gap: 1.3,

                          alignItems:
                            "center",
                        }}
                      >
                        <FormControl
                          fullWidth
                          required
                          disabled={
                            viewMode
                          }
                        >
                          <InputLabel>
                            Matière / composant
                          </InputLabel>

                          <Select
                            label="Matière / composant"
                            value={
                              item.material_product_id
                            }
                            onChange={(
                              event
                            ) =>
                              updateMaterialLine(
                                index,
                                "material_product_id",
                                event.target
                                  .value
                              )
                            }
                          >
                            {bomComponents.map(
                              (product) => (
                                <MenuItem
                                  key={
                                    product.id
                                  }
                                  value={
                                    product.id
                                  }
                                >
                                  {productLabel(
                                    product
                                  )}
                                </MenuItem>
                              )
                            )}
                          </Select>
                        </FormControl>


                        <TextField
                          label="Quantité"
                          type="number"
                          required
                          disabled={
                            viewMode
                          }
                          value={
                            item.quantity
                          }
                          inputProps={{
                            min: 0.0001,
                            step: "any",
                          }}
                          onChange={(
                            event
                          ) =>
                            updateMaterialLine(
                              index,
                              "quantity",
                              event.target
                                .value
                            )
                          }
                        />


                        <TextField
                          label="Unité"
                          disabled
                          value={unitLabel(
                            item.unit ||
                              getProductUnit(
                                component
                              )
                          )}
                        />


                        <TextField
                          label="Perte %"
                          type="number"
                          disabled={
                            viewMode
                          }
                          value={
                            item.waste_percent
                          }
                          inputProps={{
                            min: 0,
                            max: 100,
                            step: "any",
                          }}
                          onChange={(
                            event
                          ) =>
                            updateMaterialLine(
                              index,
                              "waste_percent",
                              event.target
                                .value
                            )
                          }
                        />


                        <TextField
                          label="Notes"
                          disabled={
                            viewMode
                          }
                          value={
                            item.notes
                          }
                          onChange={(
                            event
                          ) =>
                            updateMaterialLine(
                              index,
                              "notes",
                              event.target
                                .value
                            )
                          }
                        />


                        {!viewMode && (
                          <Tooltip title="Supprimer la ligne">
                            <span>
                              <IconButton
                                color="error"
                                disabled={
                                  form.items
                                    .length <=
                                  1
                                }
                                onClick={() =>
                                  removeMaterialLine(
                                    index
                                  )
                                }
                              >
                                <DeleteOutlineRoundedIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </Box>
                    </Paper>
                  )
                }
              )}
            </Stack>


            <Divider sx={{ my: 3 }} />


            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Notes / instructions de production"
              disabled={viewMode}
              value={form.notes}
              onChange={(event) =>
                setForm(
                  (previous) => ({
                    ...previous,
                    notes:
                      event.target
                        .value,
                  })
                )
              }
              placeholder="Ex. étapes de mélange, température, contrôle qualité, instructions techniques..."
            />
          </DialogContent>


          <DialogActions
            sx={{ p: 2 }}
          >
            <Button
              onClick={closeDialog}
              disabled={saving}
            >
              {viewMode
                ? "Fermer"
                : "Annuler"}
            </Button>


            {!viewMode && (
              <Button
                variant="contained"
                onClick={
                  handleSave
                }
                disabled={saving}
                startIcon={
                  saving
                    ? (
                        <CircularProgress
                          size={18}
                          color="inherit"
                        />
                      )
                    : null
                }
              >
                {saving
                  ? "Enregistrement..."
                  : form.id
                  ? "Enregistrer les modifications"
                  : "Créer la nomenclature"}
              </Button>
            )}
          </DialogActions>
        </Dialog>


        {/* SNACKBAR */}

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() =>
            setSnackbar(
              (previous) => ({
                ...previous,
                open: false,
              })
            )
          }
          anchorOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <Alert
            variant="filled"
            severity={
              snackbar.severity
            }
            onClose={() =>
              setSnackbar(
                (previous) => ({
                  ...previous,
                  open: false,
                })
              )
            }
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </MainLayout>
  )
}